// src/pages/PassengerHome.jsx
import React, {
  useEffect,
  useState,
  useRef
} from "react";

import { ref, set, get, onValue, update, push, runTransaction } from "firebase/database";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import { FaMap, FaUser } from "react-icons/fa";
import "./passenger.css";
import { FaWhatsapp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "../i18n";
import { formatCurrency } from "../utils/formatCurrency";
import DriverProfileOverlay from "../components/DriverProfileOverlay";
import PassengerSocialProfile from "../components/PassengerSocialProfile";
import Market from "../components/Market";
import RentalBookingForm from "../components/RentalBookingForm";

// ----------------- Leaflet Marker Fix -----------------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// ----------------- Recenter Map -----------------
const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, map.getZoom(), { animate: true });
  }, [position, map]);
  return null;
};

// ----------------- Force Map to Calculate Size -----------------
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      window.dispatchEvent(new Event('resize'));
    }, 800); 
    return () => clearTimeout(timer);
  }, [map]);
  return null;
};

// ----------------- Route Machine (Passenger -> Destination) -----------------
const RouteMachine = ({ pickup, destination, setDistance }) => {
  const map = useMap();
  useEffect(() => {
    const pLat = pickup?.[0] ?? pickup?.lat;
    const pLng = pickup?.[1] ?? pickup?.lng;
    const dLat = destination?.[0] ?? destination?.lat;
    const dLng = destination?.[1] ?? destination?.lng;

    if (!map || pLat === undefined || dLat === undefined) return;
    
    let routeLine;
    const router = L.Routing.osrmv1({
      serviceUrl: "https://router.project-osrm.org/route/v1",
    });

    router.route(
      [
        L.Routing.waypoint(L.latLng(pLat, pLng)),
        L.Routing.waypoint(L.latLng(dLat, dLng)),
      ],
      (err, routes) => {
        if (err || !routes.length) return;
        const route = routes[0];

        routeLine = L.polyline(
route.coordinates.map(c =>
  Array.isArray(c)
    ? [c[0], c[1]]
    : [c.lat, c.lng]
),

{
  color: "#1e90ff",
  weight: 6,
  className: "blue-route-glow"
}          
        ).addTo(map);

        const km = route.summary.totalDistance / 1000;
        setDistance(km.toFixed(2));
      }
    );

    return () => {
      if (routeLine && map.hasLayer(routeLine)) map.removeLayer(routeLine);
    };
  }, [map, pickup, destination, setDistance]);

  return null;
};

// ----------------- Driver Route (Driver -> Passenger) -----------------
const DriverRoute = ({ driverPos, passengerPos, setEta }) => {
  const map = useMap();
  useEffect(() => {
    const dLat = driverPos?.[0] ?? driverPos?.lat;
    const dLng = driverPos?.[1] ?? driverPos?.lng;
    const pLat = passengerPos?.[0] ?? passengerPos?.lat;
    const pLng = passengerPos?.[1] ?? passengerPos?.lng;

if (dLat == null || pLat == null || dLng == null || pLng == null) return;    
    let driverLine;
    const router = L.Routing.osrmv1({
      serviceUrl: "https://router.project-osrm.org/route/v1",
    });

    router.route(
      [
        L.Routing.waypoint(L.latLng(dLat, dLng)),
        L.Routing.waypoint(L.latLng(pLat, pLng)),
      ],
      (err, routes) => {
        if (err || !routes.length) return;
        const route = routes[0];
     driverLine = L.polyline(
  route.coordinates,
  {
    color: "#ff3b30",
    weight: 5,
    dashArray: "8,12",
    className: "red-route-glow"
  }
).addTo(map);

        const km = route.summary.totalDistance / 1000;
        const eta = Math.ceil((km / 40) * 60); 
        setEta(eta);
      }
    );

    return () => {
      if (driverLine && map.hasLayer(driverLine)) map.removeLayer(driverLine);
    };
  }, [driverPos, passengerPos, setEta, map]);

  return null;
};

// ----------------- Destination / Pickup Selector -----------------
const DestinationSelector = ({
  setPassengerLocation,
  setDestinationCoords,
  selectingPickup,
  selectingDestination,
  setSelectingPickup,
  setSelectingDestination,
  setPickupMarker,
  setDropoffMarker,
  setAnimatePickup,
  setAnimateDropoff,

  setRentalPickupLocation,
  setRentalReturnLocation,
}) => {

  const map = useMap();

  useEffect(() => {
    if (!selectingPickup && !selectingDestination) return;

    let isDragging = false;

    const handleDragStart = () => {
      isDragging = true;
    };

    const handleDragEnd = () => {
      setTimeout(() => {
        isDragging = false;
      }, 150);
    };


const handleClick = (e) => {
  if (isDragging) return;

  const coords = [e.latlng.lat, e.latlng.lng];

  // ---------------- PICKUP ----------------
  if (selectingPickup) {
    setPassengerLocation(coords);
    setPickupMarker(coords); // must exist in state
setRentalPickupLocation(
  `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`
);

    setAnimatePickup(true);
    setTimeout(() => setAnimatePickup(false), 800);

    setSelectingPickup(false);
    setSelectingDestination(true);

    map.panTo(coords);
    return;
  }

  // ---------------- DROPOFF ----------------
  if (selectingDestination) {
    setDestinationCoords(coords);
    setDropoffMarker(coords); // must exist in state
setRentalReturnLocation(
  `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`
);
    setAnimateDropoff(true);
    setTimeout(() => setAnimateDropoff(false), 800);

    setSelectingDestination(false);

    map.panTo(coords);
  }
};

    map.on("dragstart", handleDragStart);
    map.on("dragend", handleDragEnd);
    map.on("click", handleClick);

    return () => {
      map.off("dragstart", handleDragStart);
      map.off("dragend", handleDragEnd);
      map.off("click", handleClick);
    };
  }, [
    map,
    selectingPickup,
    selectingDestination,
    setPassengerLocation,
    setDestinationCoords,
    setSelectingPickup,
    setSelectingDestination,
  ]);

  return null;
};

// ----------------- Passenger Home -----------------
const PassengerHome = () => {
  const [passengerLocation, setPassengerLocation] = useState(null);
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [rideDate, setRideDate] = useState("");
  const [rideTime, setRideTime] = useState("");
   const [fareInput, setFareInput] = useState(""); 
  const [rideId, setRideId] = useState(null);
  const [exclusiveDriverRoute, setExclusiveDriverRoute] = useState(null);
  const [etaMinutes, setEtaMinutes] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [savingsPercent] = useState(0.1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [distanceKm, setDistanceKm] = useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [mapView, setMapView] = useState("street");
  const [showRidePopup, setShowRidePopup] = useState(false);
  const [passengerLivePosition, setPassengerLivePosition] = useState(null);
  const [gpsWatchId, setGpsWatchId] = useState(null);
  const expiryLockRef = React.useRef(false);
  const [selectedDriverOverlay, setSelectedDriverOverlay] = useState(null);
  const [showSocialProfile, setShowSocialProfile] = useState(false);
  const [showMarket, setShowMarket] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [rentalPickupLocation, setRentalPickupLocation] = useState("");
const [rentalReturnLocation, setRentalReturnLocation] = useState("");
const [pauseMarquee, setPauseMarquee] = useState(false);
const [rentalVehicleType, setRentalVehicleType] = useState("");
const [rentalDays, setRentalDays] = useState(0);
const [pickupDate, setPickupDate] = useState("");
const [pickupTime, setPickupTime] = useState("");
const [returnDate, setReturnDate] = useState("");
const [returnTime, setReturnTime] = useState("");
const [rentalNote, setRentalNote] = useState("");
const [pickupMarker, setPickupMarker] = useState(null);
const [pickupSuggestions, setPickupSuggestions] = useState([]);
const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
const mapRef = useRef(null);
const [dropoffMarker, setDropoffMarker] = useState(null);
const [animatePickup, setAnimatePickup] = useState(false);
const [animateDropoff, setAnimateDropoff] = useState(false);
const [acceptedRidePopup, setAcceptedRidePopup] = useState(null);




const searchLocation = async (
  query,
  isPickup
) => {

  if (!query || query.length < 3) {

    if (isPickup) {
      setPickupSuggestions([]);
    } else {
      setDropoffSuggestions([]);
    }

    return;
  }

  try {

    const geoCity =
      passengerProfile?.city || "";

    const geoCountry =
      passengerProfile?.country || "";

    const localizedQuery =
      `${query}, ${geoCity}, ${geoCountry}`;

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(localizedQuery)}`
    );

    const data = await response.json();

    if (isPickup) {
      setPickupSuggestions(data);
    } else {
      setDropoffSuggestions(data);
    }

  } catch (err) {

    console.error(
      "Search location error:",
      err
    );
  }
};

const selectPickupLocation = (place) => {

  const lat = parseFloat(place.lat);
  const lng = parseFloat(place.lon);

  const coords = [lat, lng];

  setPassengerLocation(coords);

  setPickupMarker(coords);

  setRentalPickupLocation(
    place.display_name
  );

  setPickupSuggestions([]);

  setAnimatePickup(true);

  setTimeout(() => {
    setAnimatePickup(false);
  }, 800);

  if (mapRef.current) {

    mapRef.current.flyTo(
      coords,
      15,
      {
        animate: true,
        duration: 1.5,
      }
    );
  }
};

const selectDropoffLocation = (place) => {

  const lat = parseFloat(place.lat);
  const lng = parseFloat(place.lon);

  const coords = [lat, lng];

  setDestinationCoords(coords);

  setDropoffMarker(coords);

  setRentalReturnLocation(
    place.display_name
  );

  setDropoffSuggestions([]);

  setAnimateDropoff(true);

  setTimeout(() => {
    setAnimateDropoff(false);
  }, 800);

  if (mapRef.current) {

    mapRef.current.flyTo(
      coords,
      15,
      {
        animate: true,
        duration: 1.5,
      }
    );
  }
};


const [rideResponseSent, setRideResponseSent] = useState({});

const [driverOption, setDriverOption] = useState("");
  const [passengerProfile, setPassengerProfile] = useState({
    name: "", email: "", phone: "", city: "", country: "", bio: "", photoUrl: "",
  });

// 👇 NEW: Custom Alert State
  const [rideAlert, setRideAlert] = useState({ show: false, message: "", type: "success" });

  const showCustomAlert = (message, type = "success") => {
    setRideAlert({ show: true, message, type });
    // Auto-hide the alert after 4 seconds to match the CSS animation
    setTimeout(() => {
      setRideAlert({ show: false, message: "", type: "success" });
    }, 4000);
  };

  const { t, i18n } = useTranslation();
  const userCountry = passengerProfile?.country || localStorage.getItem("country") || "Papua New Guinea";

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "tpi" : "en";
    i18n.changeLanguage(newLang);
  };

  

const shouldShowRideMarker = (ride = {}) => {
  const currentPassengerId = auth.currentUser?.uid;

  if (!ride.timestamp) return false;
  if (ride.passengerId !== currentPassengerId) return false;

  const now = Date.now();
  const ONE_HOUR = 60 * 60 * 1000;

  if (
    ride.status === "completed" ||
    ride.status === "expired"
  ) {
    return false;
  }

  return now - ride.timestamp < ONE_HOUR;
};
  const [selectingPickup, setSelectingPickup] = useState(true);
  const [selectingDestination, setSelectingDestination] = useState(false);
  const [rideRequestVisible, setRideRequestVisible] = useState(false);

  const buttonStyle = {
    background: "#007bff", color: "white", border: "none", padding: "8px 16px",
    borderRadius: "8px", cursor: "pointer", fontSize: "14px", display: "flex",
    alignItems: "center", justifyContent: "center", gap: "8px",
  };

  const UnderUpgrade = ({ feature }) => (
    <div style={{ padding: "20px", marginTop: "10px", background: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: 8, textAlign: "center", fontWeight: "bold" }}>
      ⚠️ {feature} feature is under upgrade. Please check back later.
    </div>
  );

  const [activeRides, setActiveRides] = useState([]);


useEffect(() => {
  const ride = activeRides.find(
    (r) =>
      r.passengerId === auth.currentUser?.uid &&
      r.status === "accepted"
  );

  if (ride && !acceptedRidePopup) {
    setAcceptedRidePopup(ride);
  }
}, [activeRides, acceptedRidePopup]);


  useEffect(() => {
    if (!passengerLocation || !destinationCoords) return;
    const pLat = passengerLocation[0];
    const pLng = passengerLocation[1];
    const dLat = destinationCoords[0];
    const dLng = destinationCoords[1];

    const R = 6371;
    const dLatRad = (dLat - pLat) * (Math.PI / 180);
    const dLonRad = (dLng - pLng) * (Math.PI / 180);
    const a = Math.sin(dLatRad / 2) ** 2 + Math.cos(pLat * (Math.PI / 180)) * Math.cos(dLat * (Math.PI / 180)) * Math.sin(dLonRad / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    setDistanceKm(distance.toFixed(2));
  }, [passengerLocation, destinationCoords]);

  // ---------------- Active Rides with Drivers (Fast Image Fetching) ----------------
  useEffect(() => {
    const ridesRef = ref(db, "rides");
    const driversRef = ref(db, "drivers");

    let latestRides = {};
    let latestDrivers = {};

    const updateActiveRides = async () => {
      const currentPassengerId = auth.currentUser?.uid;

const acceptedRides = Object.entries(latestRides)
  .filter(([_, ride]) =>
    ride.status === "accepted" &&
    ride.driverId &&
    ride.passengerId === currentPassengerId
  )
  .map(([id, ride]) => ({ id, ...ride }));

      const enrichedRides = await Promise.all(acceptedRides.map(async (ride) => {
        let dProfile = latestDrivers[ride.driverId];

        if (!dProfile) {
          try {
            const snap = await get(ref(db, `drivers/${ride.driverId}`));
            dProfile = snap.exists() ? snap.val() : {};
          } catch (err) {
            dProfile = {};
          }
        }

        const displayPhoto = dProfile.photoURL || dProfile.photoUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png";

        return {
          ...ride,
          driverProfile: { ...dProfile, photoURL: displayPhoto } 
        };
      }));

      setActiveRides(enrichedRides);
    };

    const unsubRides = onValue(ridesRef, (snap) => {
      latestRides = snap.val() || {};
      updateActiveRides();
    });

    const unsubDrivers = onValue(driversRef, (snap) => {
      latestDrivers = snap.val() || {};
      updateActiveRides();
    });

    return () => {
      unsubRides();
      unsubDrivers();
    };
  }, []);

  // ---------------- Database Expiry Cleanup ----------------
  useEffect(() => {
    const ridesRef = ref(db, "rides");
    const unsubscribe = onValue(ridesRef, async () => {
      if (expiryLockRef.current) return;
      expiryLockRef.current = true;

      try {
        const snapshot = await get(ridesRef);
     const data = snapshot.val() || {};





        const now = Date.now();
        const ONE_HOUR = 60 * 60 * 1000;

        const updates = {};

        Object.entries(data).forEach(([id, ride]) => {
          if (!ride) return;
          if (ride.status === "expired" || ride.status === "completed") return;

          if (!ride.timestamp) {
            updates[`rides/${id}/status`] = "expired";
            updates[`rides/${id}/expiryLocked`] = true;
            return;
          }

          if (ride.status === "pending" && !ride.expiryLocked && !ride.driverId && now - ride.timestamp > ONE_HOUR) {
            updates[`rides/${id}/status`] = "expired";
            updates[`rides/${id}/expiryLocked`] = true;
          }

          if (ride.status === "accepted" && ride.driverId && now - ride.timestamp > ONE_HOUR) {
            updates[`rides/${id}/status`] = "expired";
            updates[`rides/${id}/expiryLocked`] = true;
          }
        });

        if (Object.keys(updates).length > 0) await update(ref(db), updates);
      } finally {
        setTimeout(() => { expiryLockRef.current = false; }, 3000);
      }
    });

    return () => unsubscribe();
  }, []);

useEffect(() => {
  const currentUser = auth.currentUser;
  if (!currentUser) return;

  const ridesRef = ref(db, "rides");

  const unsubscribe = onValue(ridesRef, async (snapshot) => {
    const rides = snapshot.val() || {};
    const passengerId = currentUser.uid;

    for (const [rideId, ride] of Object.entries(rides)) {
      if (
  ride.passengerId === passengerId &&
  ride.status === "completed" &&
  !ride.walletProcessed &&
  Number(ride.fare) > 0
) {
  if (ride.walletProcessed) continue;

        const savingsAmount = Number((Number(ride.fare) * 0.1).toFixed(2));
        const walletRef = ref(db, `wallets/${passengerId}`);
        const txRef = push(ref(db, `walletTransactions/${passengerId}`));

        try {
          await runTransaction(walletRef, (wallet) => {
            if (!wallet) {
              wallet = {
                balance: 0,
                totalSaved: 0,
                totalWithdrawn: 0,
                updatedAt: 0,
              };
            }

            wallet.balance = Number(wallet.balance || 0) + savingsAmount;
            wallet.totalSaved = Number(wallet.totalSaved || 0) + savingsAmount;
            wallet.updatedAt = Date.now();
            return wallet;
          });

          await set(txRef, {
            type: "savings",
            amount: savingsAmount,
            rideId,
            status: "completed",
            createdAt: Date.now(),
            note: `10% savings from completed ride`,
          });

          await update(ref(db, `rides/${rideId}`), {
            walletProcessed: true,
            walletAmount: savingsAmount,
          });
        } catch (err) {
          console.error("Wallet processing failed for ride:", rideId, err);
        }
      }
    }
  });

  return () => unsubscribe();
}, []);

  // ---------------- Fetch wallet balance & pending savings ----------------

  //........Passenger Profile Fetch...//
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snapshot = await get(ref(db, `passengers/${user.uid}`));
      const data = snapshot.val();
      
      if (data) {
        setPassengerProfile({
          name: data.name || "Passenger Name", email: data.email || "", phone: data.phone || "",
          city: data.city || "", country: data.country || "", bio: data.bio || "", photoUrl: data.photoUrl || data.photoURL || ""
        });
        if (data.position) setPassengerLocation(data.position); 
      }
    });
    return () => unsubscribe();
  }, []);

const services = [
  { id: "taxi", labelKey: "service_taxi", color: "#1e90ff" },
  { id: "bus", labelKey: "service_bus", color: "#2ecc71" },
  { id: "rental", labelKey: "service_rental", color: "#f1c40f" }, // shows "Rental Car"
  { id: "boat", labelKey: "service_boat", color: "#9b59b6" },
  { id: "delivery", labelKey: "service_delivery", color: "#e91e63" }, // shows "Delivery Truck"
  { id: "motorbike", labelKey: "service_motorbike", color: "#ff9800" },
];

  function getDistance([lat1, lng1], [lat2, lng2]) {
    const R = 6371; 
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lng2 - lng1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(2); 
  }

  useEffect(() => {
    const stopWatch = () => {
      if (gpsWatchId) {
        navigator.geolocation.clearWatch(gpsWatchId);
        setGpsWatchId(null);
        const user = auth.currentUser;
        if (user) update(ref(db, `passengers/${user.uid}`), { livePosition: null });
      }
    };
    window.addEventListener("beforeunload", stopWatch);
    return () => { window.removeEventListener("beforeunload", stopWatch); stopWatch(); };
  }, [gpsWatchId]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setPassengerLocation([position.coords.latitude, position.coords.longitude]),
        (error) => console.log("GPS on load denied or failed:", error.message),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }
  }, []);


useEffect(() => {
  const user = auth.currentUser;

  if (!user) return;

  if (!navigator.geolocation) return;

  const watchId = navigator.geolocation.watchPosition(
    async (position) => {

      const liveCoords = [
        position.coords.latitude,
        position.coords.longitude,
      ];

      // update local state
      setPassengerLivePosition(liveCoords);

      // save realtime GPS to Firebase
      await update(
        ref(db, `passengers/${user.uid}`),
        {
          livePosition: liveCoords,
          lastGpsUpdate: Date.now(),
        }
      );
    },

    (err) => {
      console.log("Passenger GPS Error:", err);
    },

    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 5000,
    }
  );

  setGpsWatchId(watchId);

  return () => {
    navigator.geolocation.clearWatch(watchId);
  };
}, []);





useEffect(() => {
  const currentUser = auth.currentUser;
  if (!currentUser) return;

  const walletRef = ref(db, `wallets/${currentUser.uid}`);

  const unsubscribe = onValue(walletRef, (snapshot) => {
    const wallet = snapshot.val() || {
      balance: 0,
      totalSaved: 0,
      totalWithdrawn: 0,
      updatedAt: 0,
    };

    setWalletBalance(Number(wallet.balance || 0));
  });

  return () => unsubscribe();
}, []);

 // ---------------- Fetch ALL registered drivers (15km limit & Local Area strictness) ----------------
  useEffect(() => {
    const driversRef = ref(db, "drivers");

return onValue(driversRef, async (snapshot) => {

      const data = snapshot.val() || {};


const ridesSnap = await get(ref(db, "rides"));
const ridesData = ridesSnap.val() || {};

      let allServiceDrivers = Object.entries(data)
.map(([id, d]) => {
  const driverRides = Object.values(ridesData).filter(
    (ride) => ride.driverId === id
  );

  return {
  id,

  ...d,

  totalRides: driverRides.length,
    completedRides: driverRides.filter(
      (ride) => ride.status === "completed"
    ).length,
  };
})

.filter((driver) => {

  const driverServices =
    Array.isArray(driver.serviceTypes)
      ? driver.serviceTypes
      : driver.serviceType
        ? [driver.serviceType]
        : [];

  return selectedService
    ? driverServices.includes(
        selectedService.id
      )
    : true;

})


if (passengerLocation) {
  allServiceDrivers = allServiceDrivers.filter((driver) => {
  const sameCountry =
  passengerProfile.country &&
  driver.country &&
  String(driver.country).trim().toLowerCase() ===
    String(passengerProfile.country).trim().toLowerCase();

const sameCity =
  !passengerProfile.city ||
  !driver.city ||
  String(driver.city).trim().toLowerCase() ===
    String(passengerProfile.city).trim().toLowerCase();

const sameProvince =
  !passengerProfile.province ||
  !driver.province ||
  String(driver.province).trim().toLowerCase() ===
    String(passengerProfile.province).trim().toLowerCase();

return sameCountry && (sameCity || sameProvince);
  });
}

      allServiceDrivers.sort((a, b) => (b.online === true ? 1 : 0) - (a.online === true ? 1 : 0));
      setDrivers(allServiceDrivers);
    });
  }, [selectedService, passengerLocation, passengerProfile.city, passengerProfile.province]); 
  // 👆 Notice I added the city/province to the dependency array so it updates properly!

  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await response.json();
      return data.display_name.split(',').slice(0, 2).join(',') || "Unknown Street";
    } catch (error) {
      console.error("Geocoding error:", error);
      return "Selected Location";
    }
  };

const handleRequestRide = async (driverId = null) => {
  const isRental = selectedService?.id === "rental";

  if (isRental) {
    if (
      !selectedService ||
      !fareInput ||
      !rentalPickupLocation ||
      !rentalReturnLocation ||
      !rentalVehicleType ||
      !pickupDate ||
      !pickupTime ||
      !returnDate ||
      !returnTime ||
      !passengerLocation
    ) {
showCustomAlert(
  t("complete_all_rental_fields"),
  "error"
);      
      return;
    }
  } else {
    if (
      !selectedService ||
      !passengerLocation ||
      !destinationCoords ||
      !fareInput ||
      !rideDate ||
      !rideTime
    ) {
showCustomAlert(
  t("complete_all_ride_fields"),
  "error"
);      
      return;
    }
  }

  try {
    const currentPassengerId = auth.currentUser?.uid;
    if (!currentPassengerId) {
      alert("Passenger session not found.");
      return;
    }

    const passengerSnapshot = await get(ref(db, `passengers/${currentPassengerId}`));
    const passengerData = passengerSnapshot.val();

    if (!passengerData) {
      alert("Passenger profile not found!");
      return;
    }

    // ✅ BLOCK DUPLICATE ACTIVE REQUESTS FOR SAME SERVICE
    const ridesSnap = await get(ref(db, "rides"));
    const ridesData = ridesSnap.val() || {};

    const existingActiveRide = Object.entries(ridesData).find(([_, ride]) => {
      if (!ride) return false;

      const samePassenger = ride.passengerId === currentPassengerId;
      const sameService = ride.serviceType === selectedService.id;
      const stillActive = ride.status === "pending" || ride.status === "accepted";

      // if direct request, only treat same targeted driver as duplicate
      const sameTarget =
        (ride.exclusiveDriver || null) === (driverId || null);

      return samePassenger && sameService && stillActive && sameTarget;
    });

    if (existingActiveRide) {
      showCustomAlert(
        `You already have an active ${selectedService.id} request.`,
        "error"
      );
      return;
    }

    let pickupAddr = "";
    let dropoffAddr = "";

    if (isRental) {
      pickupAddr = rentalPickupLocation;
      dropoffAddr = rentalReturnLocation;
    } else {
      pickupAddr = await getAddressFromCoords(
        passengerLocation[0],
        passengerLocation[1]
      );
      dropoffAddr = await getAddressFromCoords(
        destinationCoords[0],
        destinationCoords[1]
      );
    }

    const rideRef = push(ref(db, "rides"));

    await set(rideRef, {
      passengerId: currentPassengerId,
      passengerName: passengerData.name || "Passenger",
      passengerPhoto: passengerData.photoUrl || passengerData.photoURL || "",
      passengerPhone: passengerData.phone || "",
      city: passengerData.city || "",
      province: passengerData.province || "",
      country: passengerData.country || "",

      serviceType: selectedService.id,
      requestScope: driverId ? "direct" : "service",
      exclusiveDriver: driverId || null,

      pickupLocation: isRental ? rentalPickupLocation : "",
      returnLocation: isRental ? rentalReturnLocation : "",
      vehicleType: isRental ? rentalVehicleType : "",
      rentalDays: isRental ? Number(rentalDays || 0) : 0,
      totalCost: isRental
        ? Number(fareInput || 0) * Number(rentalDays || 0)
        : Number(fareInput),
      rentalNote: isRental ? rentalNote : "",
      driverOption: isRental ? driverOption : "",

      pickup: passengerLocation
        ? {
            lat: Number(passengerLocation[0]),
            lng: Number(passengerLocation[1]),
          }
        : null,

      destinationCoords: destinationCoords
        ? {
            lat: Number(destinationCoords[0]),
            lng: Number(destinationCoords[1]),
          }
        : null,

      pickupAddress: pickupAddr,
      dropoffAddress: dropoffAddr,

      distanceKm: !isRental ? Number(distanceKm || 0) : 0,
      fare: Number(fareInput),

      date: isRental ? pickupDate : rideDate,
      time: isRental ? pickupTime : rideTime,
      returnDate: isRental ? returnDate : "",
      returnTime: isRental ? returnTime : "",

      status: "pending",
      driverId: null,
      timestamp: Date.now(),
      tripStarted: false,
      tripEnded: false,
      escrowHold: true,
      expiryLocked: false,
    });

    setRideId(rideRef.key);

    if (driverId) {
      const driver = drivers.find((d) => d.id === driverId);
      if (driver && driver.position) {
        setExclusiveDriverRoute(driver.position);
      }
    }




    showCustomAlert(
      driverId ? t("direct_ride_requested") : t("ride_broadcasted"),
      "success"
    );

    setFareInput("");
    setRideDate("");
    setRideTime("");
setRentalPickupLocation("");    
    setRentalReturnLocation("");
    setRentalVehicleType("");
    setRentalDays(0);
    setRentalNote("");
    setPickupDate("");
    setPickupTime("");
    setReturnDate("");
    setReturnTime("");
    setDriverOption("");

    setSelectedService(null);
    setRideRequestVisible(false);
  } catch (error) {
    console.error("Error requesting ride:", error);
    showCustomAlert("Failed to request ride. Try again.", "error");
  }
};

     return (
    <div className={`relative h-screen w-screen overflow-hidden ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}>
      
      {/* 👇 NEW: Render the custom animated alert if it is triggered */}
    {rideAlert.show && (
  <div className={`custom-ride-alert ${rideAlert.type}`}>
    <span className="alert-icon">
      {rideAlert.type === "success" ? "✅" : "⚠️"}
    </span>
    <span className="alert-text">{rideAlert.message}</span>
  </div>
)}

{acceptedRidePopup && (
  <div className="ride-popup-overlay">
    <div className="ride-popup-box">
      
<h3>🚗 {t("driver_accepted_ride")}</h3>
      <img
        src={acceptedRidePopup.driverProfile?.photoURL}
        style={{ width: 60, height: 60, borderRadius: "50%" }}
      />

      <p><b>{acceptedRidePopup.driverProfile?.name}</b></p>
      <p>📍 Driver is on the way</p>

     <button
  onClick={async () => {

    const rideKey = acceptedRidePopup?.id;

    if (!rideKey) {
      console.log("Ride ID missing");
      return;
    }

   await update(
  ref(db, `rides/${rideKey}`),
  {
    passengerResponse: "rejected",
    driverNotifiedRejected: false,
  }
);

    showCustomAlert(
      "Ride cancelled",
      "error"
    );

  }}
  style={{
    background: "red",
    color: "#fff",
    padding: 10
  }}
>
{t("ride_cancelled")}
</button>

  <button
  onClick={async () => {

   const rideKey = acceptedRidePopup?.id;

    if (!rideKey) {
      console.log("Ride ID missing");
      return;
    }

    await update(
      ref(db, `rides/${rideKey}`),
      {
        passengerResponse: "accepted",
      }
    );

    showCustomAlert(
t("ride_confirmed_success"),      
      "success"
    );

setTimeout(() => {
  setAcceptedRidePopup(null);
}, 5000);    

    console.log("Ride Key:", rideKey);
  }}
>
  OK
</button>
    </div>
  </div>
)}




      {/* ... the rest of your UI (sidebar button, map, etc.) continues here ... */} 
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ position: "fixed", top: "20px", left: "20px", zIndex: 1100, background: "#007bff", color: "white", border: "none", width: "45px", height: "45px", borderRadius: "50%", fontSize: "20px", cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease" }}
        >
          ☰
        </button>
      )}

      <button
        onClick={() => {
          if (!navigator.geolocation) return;
          const user = auth.currentUser;
if (!user || !navigator.geolocation) return;          
          if (gpsWatchId) {
            navigator.geolocation.clearWatch(gpsWatchId);
            setGpsWatchId(null);
            update(ref(db, `passengers/${user.uid}`), { livePosition: null });
            return;
          }
          const watchId = navigator.geolocation.watchPosition(
            (pos) => {
              const coords = [pos.coords.latitude, pos.coords.longitude];
              setPassengerLivePosition(coords);
              update(ref(db, `passengers/${user.uid}`), { livePosition: coords });
              if (rideId) {
                update(ref(db, `rides/${rideId}`), { passengerPosition: coords, passengerLastUpdated: Date.now(), sessionPassengerId: auth.currentUser.uid });
              }
            },
            () => alert("GPS permission denied"),
            { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
          );
          setGpsWatchId(watchId);
        }}
        style={{ position: "absolute", top: "15px", right: "15px", zIndex: 1000, backgroundColor: gpsWatchId ? "#00c853" : "#0a4ecc", color: "#fff", width: "50px", height: "50px", borderRadius: "50%", border: "none", cursor: "pointer", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
      >
        📍
      </button>

{/* Floating Animated Driver Header */}

{drivers.length > 0 && (
  <div
    style={{
      position: "absolute",
      top: "15px",
      left: sidebarOpen ? "200px" : "80px",

      right: "15px",
      zIndex: 998,
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      pointerEvents: "auto",
      maskImage:
        "linear-gradient(to right, transparent, black 15%)",
      WebkitMaskImage:
        "linear-gradient(to right, transparent, black 15%)",
      transition: "left 0.3s ease",
    }}
  >
    <style>{`
      .marquee-track {
        display: inline-flex;
        gap: 15px;
        animation: marqueeAnim 20s linear infinite;
      }

      .marquee-track:hover {
        animation-play-state: paused;
      }

      @keyframes marqueeAnim {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-50%);
        }
      }
    `}</style>

<div
  className="marquee-track"
  style={{
    pointerEvents: "none",
  }}
>      
      {(drivers || []).map((driver, index) => (
        <div
  key={`${driver.id}-${index}`}
  className="driver-avatar"

  onClick={(e) => {
    e.stopPropagation();

    console.log("Driver clicked:", driver);

    setSelectedDriverId(driver.id);
  }}

  style={{
    pointerEvents: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    width: "55px",
  }}
>

        
          {/* DRIVER RING */}
          <div
            className={`driver-ring ${
              driver.online
                ? "online"
                : "offline"
            }`}
          >
            <img
              src={
                driver.photoURL ||
                driver.photoUrl ||
                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
              }
              alt={driver.name}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                objectFit: "cover",
                position: "relative",
                zIndex: 2,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
)}

<MapContainer
  whenCreated={(map) => {
    mapRef.current = map;
  }}
  key={i18n.language}
  center={passengerLocation || [-6.73, 147.00]} 
  zoom={passengerLocation ? 15 : 12}
  style={{ position: "absolute", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 0 }}
>
        <MapResizer />
<TileLayer
  url={
    mapView === "street"
      ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      : "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
  }
/>

        {(selectingPickup || selectingDestination) && (
 <div
  style={{
    position: "absolute",
    top: 15,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#007bff",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: 8,
    zIndex: 1000,
    fontWeight: "bold"
  }}
>
  {selectingPickup
    ? `📍 ${t("click_pickup_map")}`
    : `🏁 ${t("click_dropoff_map")}`}
</div>
        )}

        <DestinationSelector
  setDestinationCoords={setDestinationCoords}
  setPassengerLocation={setPassengerLocation}
  selectingPickup={selectingPickup}
  selectingDestination={selectingDestination}
  setSelectingPickup={setSelectingPickup}
  setSelectingDestination={setSelectingDestination}
setRentalPickupLocation={setRentalPickupLocation}
setRentalReturnLocation={setRentalReturnLocation}
  setPickupMarker={setPickupMarker}
  setDropoffMarker={setDropoffMarker}
  setAnimatePickup={setAnimatePickup}
  setAnimateDropoff={setAnimateDropoff}
/>
{passengerLivePosition && (
  <Marker
    position={passengerLivePosition}
    icon={L.divIcon({
      className: "passenger-live-marker",
      html: `
        <div style="width:56px;height:56px;border-radius:50%;overflow:hidden;border:3px solid #00c853;background:white;">
          <img src="${passengerProfile?.photoUrl || "/default-avatar.png"}" style="width:100%;height:100%;object-fit:cover;" />
        </div>
        <div style="width:14px;height:14px;background:#00c853;border-radius:50%;margin:-4px auto 0 auto;border:2px solid white;"></div>
      `,
      iconSize: [56, 72],
      iconAnchor: [28, 70],
    })}
  >
    <Popup>
      <div className="passenger-popup">
        <img
          src={passengerProfile?.photoUrl || "/default-avatar.png"}
          alt={passengerProfile?.name || "Passenger"}
        />
        <h4>{passengerProfile?.name || "Passenger"}</h4>
        <p>📧 {passengerProfile?.email}</p>
        <p>{passengerProfile?.phone}</p>
        <small>{passengerProfile?.city} {passengerProfile?.country}</small>

        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleLanguage();
          }}
          style={{
            marginTop: "10px",
            background: "#007bff",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          🌐 {i18n.language === "en" ? "Tok Pisin" : "English"}
        </button>

        <div className="popup-buttons">
          <button onClick={() => window.location.href = `tel:${passengerProfile.phone}`}>
            📞 Call
          </button>
          <button onClick={() => window.location.href = `/chat/${auth.currentUser?.uid}`}>
            💬 Chat
          </button>
        </div>
      </div>
    </Popup>
  </Marker>
)}
        {passengerLocation && destinationCoords && <RouteMachine pickup={passengerLocation} destination={destinationCoords} setDistance={setDistanceKm} />}
        {exclusiveDriverRoute && <DriverRoute driverPos={exclusiveDriverRoute} passengerPos={passengerLocation} setEta={setEtaMinutes} />}


{pickupMarker && (
  <Marker
    position={pickupMarker}
    icon={L.divIcon({
      className: "",
      html: `
  <div class="pickup-bounce ${animatePickup ? "animate" : ""}">
    📍 ${t("pickup")}
  </div>
`,


      iconSize: [80, 40],
      iconAnchor: [40, 40],
    })}
  />
)}

{dropoffMarker && (
  <Marker
    position={dropoffMarker}
    icon={L.divIcon({
      className: "",
     html: `
  <div class="dropoff-bounce ${animateDropoff ? "animate" : ""}">
    🏁 ${t("dropoff")}
  </div>
`,

      iconSize: [90, 40],
      iconAnchor: [45, 40],
    })}
  />
)}



      {/* Mini Floating Info Panel */}
        {passengerLocation && destinationCoords && (
          <div style={{
            position: "absolute",
            /* 👇 FIX: Moved from 10px down to 90px so it sits beautifully below the moving faces */
            top: "90px",
            left: "50%",
            transform: "translateX(-50%)",
background:
  "linear-gradient(135deg, rgba(13,59,102,0.95), rgba(30,144,255,0.9))",            
            color: "#fff",
            padding: "10px 20px",
            borderRadius: 10,
            zIndex: 1000,
boxShadow:
  "0 0 15px rgba(30,144,255,0.5), 0 0 30px rgba(30,144,255,0.3)",

animation:
  "popupFloat 2.5s ease-in-out infinite, popupGlow 1.5s infinite alternate",

border: "2px solid rgba(255,255,255,0.12)",

backdropFilter: "blur(10px)",            
            minWidth: 180,
            textAlign: "center",
          }}>

            <div className="live-distance">
  📍 {t('distance')}: {distanceKm} km
</div>

<div className="live-fare">
  💰 {t('fare')}: {
    fareInput
      ? formatCurrency(
          Number(fareInput),
          userCountry
        )
      : formatCurrency(
          0,
          userCountry
        )
  }
</div>

<div className="live-savings">
  🎉 10% {t('savings')}: {
    fareInput
      ? formatCurrency(
          Number(fareInput) *
          savingsPercent,
          userCountry
        )
      : formatCurrency(
          0,
          userCountry
        )
  }
</div>


          </div>
        )}

        <RecenterMap position={passengerLivePosition || passengerLocation || [-6.73, 147.00]} />

        {drivers.filter((driver) => driver.id === selectedDriverId && driver.position && driver.online).map((driver) => {
        const driverDisplayIcon = L.divIcon({
              className: "selected-driver-icon",
              html: `
                <div style="width:48px;height:48px;border-radius:50%;overflow:hidden;border:3px solid #00c853;background:#fff;box-shadow: 0 4px 10px rgba(0,200,83,0.5); transform: scale(1.1); transition: transform 0.2s;">
                  <img src="${driver.photoURL || driver.photoUrl || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}" style="width:48px;height:48px;border-radius:50%;object-fit:cover;display:block;" />
                </div>
                <div style="width:12px;height:12px;background:#00c853;border-radius:50%;margin:-8px auto 0 auto;border:2px solid white;position:relative;z-index:2;"></div>
              `,
              iconSize: [48, 48], iconAnchor: [24, 48],
            });

            return (
              <Marker key={`preview-${driver.id}`} position={
  driver.livePosition ||
  driver.position
}
              
              icon={driverDisplayIcon} zIndexOffset={2000}>
                <Popup>
                  <div className="driver-popup" style={{ textAlign: 'center', minWidth: '150px' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#18508a' }}>{driver.name || "Driver"}</h4>
                    <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>🚗 {driver.vehicle || "Standard Vehicle"}</p>
                    <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>⭐ {driver.rating || "New"}</p>
                    <button
                      style={{ background: "#007bff", color: "white", border: "none", padding: "8px", borderRadius: "8px", width: "100%", marginTop: "10px", cursor: "pointer", fontWeight: "bold" }}
                      onClick={() => {
  const matchedService = services.find((s) => s.id === driver.serviceType);
  setSelectedService(matchedService || null);
  setShowRidePopup(true);
}}
                    >
                      Request This Driver
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}


        {activeRides.map((ride) => {
          if (!shouldShowRideMarker(ride)) return null;
          const profile = ride.driverProfile || {};
          let markerPos = profile.position;
          if (!markerPos || markerPos.length !== 2) markerPos = ride.pickup ? [Number(ride.pickup.lat || ride.pickup[0]), Number(ride.pickup.lng || ride.pickup[1])] : null;
          if (!markerPos) return null;

          const distance = passengerLocation ? getDistance(markerPos, passengerLocation) : 0;
          const driverImg = profile.photoURL || profile.photoUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png";

        const driverIconWithPhoto = L.divIcon({
            className: "driver-marker-icon",
            html: `
              <div style="width:48px;height:48px;border-radius:50%;overflow:hidden;border:3px solid #0a4ecc;background:#fff;box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
                <img 
                  src="${driverImg}" 
                  style="width:48px;height:48px;border-radius:50%;object-fit:cover;display:block;"
                  onerror="this.src='https://cdn-icons-png.flaticon.com/512/149/149071.png'"
                />
              </div>
              <div style="width:12px;height:12px;background:#00c853;border-radius:50%;margin:-8px auto 0 auto;border:2px solid white;position:relative;z-index:2;"></div>
            `,
            iconSize: [48, 48], iconAnchor: [24, 48],
          });

          return (
            <Marker key={ride.id} position={markerPos} icon={driverIconWithPhoto} zIndexOffset={1000}>
              <Popup>
                <div className="driver-popup" style={{ textAlign: 'center', minWidth: '150px' }}>
                  <img src={driverImg} alt={profile.name} style={{ borderRadius: "50%", width: 60, height: 60, objectFit: "cover", border: "2px solid #0a4ecc", cursor: "pointer", marginBottom: "5px" }} onClick={() => setSelectedDriverOverlay({ id: ride.driverId, ...profile })} />
                  <h4 style={{ margin: '0' }}>{profile.name || "Driver"}</h4>
                  <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>📍 {distance} km away</p>
                  <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#00c853' }}>{t('fare')}: {formatCurrency(ride.fare || 0, userCountry)}</p>


<div style={{ display: "flex", gap: "5px", marginTop: 8 }}>

<button
  className="popup-btn accept"

  disabled={rideResponseSent[ride.id]}

  onClick={async () => {

    if (rideResponseSent[ride.id]) return;

    try {

      await update(ref(db, `rides/${ride.id}`), {
        passengerResponse: "accepted",
        driverNotifiedAccepted: false,
        respondedAt: Date.now(),
      });

      setRideResponseSent(prev => ({
        ...prev,
        [ride.id]: true
      }));

      showCustomAlert(
        "Ride accepted successfully!",
        "success"
      );

    } catch (err) {
      console.log(err);
    }
  }}

  style={{
    background: rideResponseSent[ride.id]
      ? "#9e9e9e"
      : "#28a745",

    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    cursor: rideResponseSent[ride.id]
      ? "not-allowed"
      : "pointer",

    fontSize: "10px",
    color: "white",
    opacity: rideResponseSent[ride.id] ? 0.6 : 1,
  }}
>
  ✅
</button>

 <button
  className="popup-btn cancel"

  disabled={rideResponseSent[ride.id]}

  onClick={async () => {

    if (rideResponseSent[ride.id]) return;

    try {

      await update(ref(db, `rides/${ride.id}`), {
        passengerResponse: "rejected",
        driverNotifiedRejected: false,
        respondedAt: Date.now(),
      });

      setRideResponseSent(prev => ({
        ...prev,
        [ride.id]: true
      }));

      showCustomAlert(
        "Ride cancelled!",
        "error"
      );

    } catch (err) {
      console.log(err);
    }
  }}

  style={{
    background: rideResponseSent[ride.id]
      ? "#9e9e9e"
      : "#dc3545",

    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",

    cursor: rideResponseSent[ride.id]
      ? "not-allowed"
      : "pointer",

    fontSize: "10px",
    color: "white",
    opacity: rideResponseSent[ride.id] ? 0.6 : 1,
  }}
>
  ❌
</button>

  <button
    className="popup-btn call"
    onClick={() => window.location.href = `tel:${profile.phone}`}

style={{
  background: "#007bff",
  border: "none",
  width: "42px",
  height: "42px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
  cursor: "pointer",
  fontSize: "10px",
  lineHeight: "1",
}}

  >
    📞 Call
  </button>

<button
  className="popup-btn chat"
  onClick={() => {
    window.open(
      `https://wa.me/${profile.phone?.replace(/\D/g, "")}`,
      "_blank"
    );
  }}
  style={{
    background: "#25D366",
    border: "none",
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
     fontSize: "10px",
  lineHeight: "1",
  }}
>
  <FaWhatsapp size={20} color="#fff" />
</button>


                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>


{selectedDriverId && (
<DriverProfileOverlay
  driver={
    drivers.find(
      (d) =>
        String(d.id) ===
        String(selectedDriverId)
    )
  }

  viewerRole="passenger"

  onClose={() =>
    setSelectedDriverId(null)
  }
/>
)}


     {/* SIDEBAR */}
      <div className={`sidebar-menu ${sidebarOpen ? "open" : "collapsed"} ${darkMode ? "dark-mode" : ""}`}>

       <button
  className="sidebar-toggle-btn"
  onClick={() => setSidebarOpen(!sidebarOpen)}
>
  {sidebarOpen ? t("back_to_map") : "☰"}
</button>

        {sidebarOpen && (
          <>
            <div className="profile-header" onClick={() => { setShowSocialProfile(true); setSidebarOpen(false); }}>
              {passengerProfile.photoUrl ? (
                <img src={passengerProfile.photoUrl} alt={passengerProfile.name || t('passenger')} />
              ) : (
                <div className="profile-placeholder">👤</div>
              )}
              {/* 👇 Visible Translated Label */}
              <small style={{ color: "#aaa", fontWeight: "bold", textTransform: "uppercase", fontSize: '10px' }}>
                {t('passenger') || "Passenger"}
              </small>
              <h3>{passengerProfile.name || "User"}</h3>
              <p>{passengerProfile.phone}</p>
              <small>{passengerProfile.city} {passengerProfile.country}</small>
            </div>

            <h4 className="sidebar-section-title">{t('menu')}</h4>
{!rideRequestVisible && (
<ul className="sidebar-menu-links">

<li onClick={() => setRideRequestVisible(true)}>
  🚖 {t('ride_requests')}
</li>              
              
             <li 
                onClick={() => {
                  const myActiveRide = activeRides.find((r) => r.passengerId === auth.currentUser?.uid && r.driverProfile?.phone);
                  if (myActiveRide) {
                    window.open(`https://wa.me/${myActiveRide.driverProfile.phone.replace(/\D/g, "")}`, "_blank");
                  } else {
                    alert(t('no_active_ride') || "You don't have an active ride.");
                  }
                }}
                style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", color: "#25D366", fontWeight: "600" }}
              >
                <FaWhatsapp size={20} /> {t('chat') || "Chat"}
              </li>
              <li onClick={() => { setShowMarket(true); setSidebarOpen(false); }}>🛒 {t('kinamarket')}</li>
              <li onClick={() => window.open("https://wa.me/67582164931", "_blank")} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", color: "#25D366", fontWeight: "600" }}>
                <FaWhatsapp size={20} /> {t('support') || "Support"}
              </li>

              <li onClick={() => { auth.signOut(); window.location.href = "/"; }}>
                🔒 {t('logout')}
              </li>

              {/* 🌐 Language Selector Dropdown */}
              <li style={{ marginTop: "10px", padding: "0", display: "flex", flexDirection: "column", gap: "5px", border: "none", background: "transparent" }}>
                <label style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", paddingLeft: "5px", fontWeight: "bold" }}>
                  🌐 Language
                </label>
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.4)",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    appearance: "none" 
                  }}
                >
                  <option value="en" style={{ color: "#333" }}>🇬🇧 English</option>
                  <option value="tpi" style={{ color: "#333" }}>🇵🇬 Tok Pisin</option>
                  <option value="fr" style={{ color: "#333" }}>🇫🇷 Français</option>
                  <option value="es" style={{ color: "#333" }}>🇪🇸 Español</option>
                  <option value="pt" style={{ color: "#333" }}>🇵🇹 Português</option>
                  <option value="zh" style={{ color: "#333" }}>🇨🇳 中文 (Chinese)</option>
                  <option value="ar" style={{ color: "#333" }}>🇸🇦 العربية (Arabic)</option>
                  <option value="ru" style={{ color: "#333" }}>🇷🇺 Русский (Russian)</option>
                  <option value="hi" style={{ color: "#333" }}>🇮🇳 हिन्दी (Hindi)</option>
                  <option value="id" style={{ color: "#333" }}>🇮🇩 Bahasa Indonesia</option>
                </select>
              </li>
            </ul>
)}


<div
  style={{
    marginTop: "auto",
    paddingTop: "25px",
    marginBottom: "20px",

    textAlign: "center",
    fontSize: "11px",
    color: "#dbeafe",
    padding: "10px",
    borderTop: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.05)",
    borderRadius: "10px",
    backdropFilter: "blur(6px)",
  }}
>
  © 2026 KinaTrip Technologies Ltd.
  <br />
  All Rights Reserved.
</div>


           {rideRequestVisible && (
<div
  className="service-overlay-panel"
  style={{
    height: "100vh",
    overflowY: "auto",
    paddingBottom: "220px",
    WebkitOverflowScrolling: "touch",
    scrollBehavior: "smooth",
  }}
>

<button
  onClick={() => {
    setRideRequestVisible(false);
    setSelectedService(null);
  }}
  style={{
    background: "#1e293b",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: "10px",
    marginBottom: "15px",
    cursor: "pointer",
    fontWeight: "bold",
    width: "100%",
  }}
>
{t("back_to_menu")}
</button>

    {!selectedService ? (
      <>
        <h4>{t("select_service")}</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setSelectedService(service)}
              style={{
                background: service.color,
                color: "#fff",
                border: "none",
                padding: "10px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {t(service.labelKey)}
            </button>
          ))}
        </div>
      </>
    ) : selectedService.id === "rental" ? (
      
  <RentalBookingForm
  t={t}
  userCountry={userCountry}

  distanceKm={distanceKm}
  etaMinutes={etaMinutes}
  fareInput={fareInput}
  setFareInput={setFareInput}
  rideDate={rideDate}
  setRideDate={setRideDate}
  rideTime={rideTime}
  setRideTime={setRideTime}
  rentalPickupLocation={rentalPickupLocation}
  setRentalPickupLocation={setRentalPickupLocation}
  rentalReturnLocation={rentalReturnLocation}
  setRentalReturnLocation={setRentalReturnLocation}
  rentalVehicleType={rentalVehicleType}
  setRentalVehicleType={setRentalVehicleType}
  rentalDays={rentalDays}
  setRentalDays={setRentalDays}
  rentalNote={rentalNote}
  setRentalNote={setRentalNote}
  handleRequestRide={handleRequestRide}
  setRideRequestVisible={setRideRequestVisible}
  setSelectedService={setSelectedService}

  // 🔥 THIS PART FIXES YOUR ERROR
  pickupDate={pickupDate}
  setPickupDate={setPickupDate}
  pickupTime={pickupTime}
  setPickupTime={setPickupTime}
  returnDate={returnDate}
  setReturnDate={setReturnDate}
  returnTime={returnTime}
  setReturnTime={setReturnTime}
  driverOption={driverOption}
  setDriverOption={setDriverOption}
/>

    ) : (
      <>
      
        <h4>{t(selectedService.labelKey)} {t("booking")}</h4>

        <input placeholder={t("distance") + " KM"} value={distanceKm} readOnly />
        {etaMinutes && <div>ETA: {etaMinutes} min</div>}




{/* PICKUP LOCATION */}
<input
  type="text"
placeholder={t("pickup_placeholder")}
  value={rentalPickupLocation}
 onChange={(e) => {

  const value =
    e.target.value;

  setRentalPickupLocation(
    value
  );

  searchLocation(
    value,
    true
  );
}}




  onFocus={() => {
    setSelectingPickup(true);
    setSelectingDestination(false);
  }}
  style={{
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    marginBottom: "10px",
    fontSize: "14px",
  }}
/>
{pickupSuggestions.length > 0 && (
  <div className="location-suggestions">

    {pickupSuggestions.map((place, index) => (

      <div
        key={index}
        className="location-item"
        onClick={() =>
          selectPickupLocation(place)
        }
      >
        📍 {place.display_name}
      </div>

    ))}

  </div>
)}





{/* DROP OFF LOCATION */}
<input
  type="text"
placeholder={t("dropoff_placeholder")}
  value={rentalReturnLocation}
  onChange={(e) => {

  const value =
    e.target.value;

  setRentalReturnLocation(
    value
  );

  searchLocation(
    value,
    false
  );
}}


  onFocus={() => {
    setSelectingPickup(false);
    setSelectingDestination(true);
  }}
  style={{
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    marginBottom: "10px",
    fontSize: "14px",
  }}
/>
{dropoffSuggestions.length > 0 && (
  <div className="location-suggestions">

    {dropoffSuggestions.map((place, index) => (

      <div
        key={index}
        className="location-item"
        onClick={() =>
          selectDropoffLocation(place)
        }
      >
        🏁 {place.display_name}
      </div>

    ))}

  </div>
)}

        <input
placeholder={t("enter_fare")}          
          type="number"
          value={fareInput}
          onChange={(e) => setFareInput(e.target.value)}
        />

       <input
  type="date"
  value={rideDate}
  onChange={(e) => setRideDate(e.target.value)}
/>

<input
  type="time"
  value={rideTime}
  onChange={(e) => setRideTime(e.target.value)}
/>

       <button
  style={{
    backgroundColor: "#38bdf8", // 🔥 nicer sky blue
    color: "#fff",
    border: "none",
    padding: "10px",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer"
  }}
  onClick={() => {
    handleRequestRide();
    setRideRequestVisible(false);
  }}
>
  {t("request_ride")}
</button>

        <button
          style={{ background: "#23e72d", marginTop: 4 }}
          onClick={() => setSelectedService(null)}
        >
          {t("back")}
        </button>

        <button
          style={{ background: "#f08be2", marginTop: 4 }}
          onClick={() => setRideRequestVisible(false)}
        >
          {t("cancel")}
        </button>
      </>
    )}
    
  </div>
)}

        
          </>
        )}
      </div>

      {showRidePopup && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 12, width: 320, maxHeight: "90vh", overflowY: "auto" }}>
            {!selectedService ? (
              <>
                <h3>{t("select_service")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {services.map((service) => (
                    <button key={service.id} onClick={() => setSelectedService(service)} style={{ background: service.color, color: "#fff", border: "none", padding: "10px", borderRadius: 8, cursor: "pointer", fontWeight: "bold" }}>{t(service.labelKey)}</button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h3>{t(selectedService.labelKey)} {t("booking")}</h3>
                <input placeholder={t("distance") + " KM"} value={distanceKm} readOnly />
                {etaMinutes && <div>ETA: {etaMinutes} min</div>}
                <input placeholder={t("fare")} type="number" value={fareInput} onChange={(e) => setFareInput(e.target.value)} />
                <input type="date" value={rideDate} onChange={(e) => setRideDate(e.target.value)} />
                <input type="time" value={rideTime} onChange={(e) => setRideTime(e.target.value)} />
                <button style={{ background: "#28a745", color: "#fff", marginTop: 8, padding: 8, borderRadius: 6, border: "none", cursor: "pointer", width: "100%" }} onClick={() => { handleRequestRide(); setShowRidePopup(false); }}>{t("request_ride")}</button>
                <button style={{ background: "#ccc", marginTop: 6, padding: 8, borderRadius: 6, border: "none", cursor: "pointer", width: "100%" }} onClick={() => setSelectedService(null)}>{t("back")}</button>
              </>
            )}
            <button style={{ marginTop: 10, width: "100%", background: "#e74c3c", color: "#fff", padding: 8, borderRadius: 6, border: "none", cursor: "pointer" }} onClick={() => { setShowRidePopup(false); setSelectedService(null); }}>{t("close")}</button>
          </div>
        </div>
      )}

      {selectedDriverOverlay && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 3000, background: "white", overflowY: "auto" }}>
          <DriverProfileOverlay driver={selectedDriverOverlay} onClose={() => setSelectedDriverOverlay(null)} />
        </div>
      )}

      {showSocialProfile && (
        <PassengerSocialProfile passengerProfile={passengerProfile} onClose={() => setShowSocialProfile(false)} />
      )}

      {showMarket && (
        <Market onClose={() => setShowMarket(false)} />
      )}

{/* --- MAIN BOTTOM NAVIGATION --- */}
<div style={{ 
  position: 'fixed',
  bottom: 0,
  width: '100%',
  height: '45px',
  background: 'white',
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  zIndex: 1000,
  borderTop: '1px solid #ddd',
  paddingBottom: 'env(safe-area-inset-bottom)'
}}>
<div
  onClick={() => {
    setShowMarket(false);
    setShowSocialProfile(false);
    setSelectedDriverOverlay(null);
    setShowRidePopup(false);
    setMapView((prev) => (prev === "street" ? "topo" : "street"));
  }}
  style={{ textAlign: 'center', cursor: 'pointer' }}
>

  <FaMap color={!showSocialProfile && !showMarket ? '#007bff' : '#888'} size={16} />
  <div style={{ fontSize: '8px', color: !showSocialProfile && !showMarket ? '#007bff' : '#888' }}>
    {mapView === "street" ? "Topo" : "Street"}
  </div>
</div>

  <div
    onClick={() => {
      setShowSocialProfile(true);
      setShowMarket(false);
      setSelectedDriverOverlay(null);
      setShowRidePopup(false);
    }}
    style={{ textAlign: 'center', cursor: 'pointer' }}
  >
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <FaUser color={showSocialProfile ? '#ff416c' : '#888'} size={16} />
      {walletBalance > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-5px',
            right: '-10px',
            background: '#28a745',
            color: 'white',
            borderRadius: '50%',
            padding: '2px 5px',
            fontSize: '9px'
          }}
        >
          K
        </span>
      )}
    </div>
    <div style={{ fontSize: '10px', color: showSocialProfile ? '#ff416c' : '#888' }}>
      {t('Profile')}
    </div>
  </div>
</div>
    </div> // End of main relative div
  ); // End of return
}; // End of PassengerHome

export default PassengerHome;





